export type SubsidiaryRecord = {
  rcid: number;
  name: string;
  total: number;
  hasSubsidiaries: boolean;
};

export type SubsidiaryTreeNode = SubsidiaryRecord & {
  depth: number;
  children: SubsidiaryTreeNode[];
  isOther?: boolean;
  /** The companies an Other bucket folds together; its children are theirs. */
  members?: SubsidiaryTreeNode[];
};

type ExpandSubsidiariesOptions = {
  root: SubsidiaryRecord;
  depth: number;
  /** Direct subsidiaries of every parent on one level, keyed by its rcid. */
  fetchLevel: (
    parentRcids: number[]
  ) => Promise<ReadonlyMap<number, SubsidiaryRecord[]>>;
  maxParentsPerLevel: number;
  maxChildrenPerParent: number;
  maxNodes: number;
};

const byTotalDescending = (a: SubsidiaryRecord, b: SubsidiaryRecord) =>
  b.total - a.total;

export async function expandSubsidiaries({
  root,
  depth,
  fetchLevel,
  maxParentsPerLevel,
  maxChildrenPerParent,
  maxNodes,
}: ExpandSubsidiariesOptions): Promise<SubsidiaryTreeNode> {
  const rootNode: SubsidiaryTreeNode = { ...root, depth: 0, children: [] };
  const visited = new Set<number>([root.rcid]);
  let nodeCount = 1;
  let frontier: SubsidiaryTreeNode[] = [rootNode];

  for (
    let level = 1;
    level <= depth && frontier.length > 0 && nodeCount < maxNodes;
    level++
  ) {
    const parents = frontier
      .filter((node) => node.hasSubsidiaries)
      .sort(byTotalDescending)
      .slice(0, maxParentsPerLevel);
    if (parents.length === 0) break;

    const childLists = await fetchLevel(
      parents.map((parent) => parent.rcid)
    ).catch((error) => {
      if (level === 1) throw error;
      return new Map<number, SubsidiaryRecord[]>();
    });

    const nextFrontier: SubsidiaryTreeNode[] = [];
    for (const parent of parents) {
      const candidates = [...(childLists.get(parent.rcid) ?? [])].sort(
        byTotalDescending
      );
      for (const child of candidates) {
        if (parent.children.length >= maxChildrenPerParent) break;
        if (nodeCount >= maxNodes) break;
        if (visited.has(child.rcid)) continue;
        visited.add(child.rcid);
        nodeCount += 1;
        const node: SubsidiaryTreeNode = {
          ...child,
          depth: level,
          children: [],
        };
        parent.children.push(node);
        nextFrontier.push(node);
      }
    }
    frontier = nextFrontier;
  }

  return rootNode;
}
